import { AlertTriangleIcon, MessageCircleWarningIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Feedback,
  FeedbackAction,
  FeedbackContent,
  FeedbackDescription,
  FeedbackEventId,
  FeedbackImage,
  FeedbackLayout,
  FeedbackTitle,
} from '@/components/ui/feedback'
import { openFeedbackDialog } from '@/helpers/sentry/open-feedback-dialog'
import useTranslator from '@/hooks/translation'
import { cn } from '@/lib/utils'

import { FeedbackRetryButton } from './retry-button'

interface FeedbackUnknownErrorProps {
  layout?: FeedbackLayout
  title?: string
  description?: string
  eventId?: string
  showReportButton?: boolean
  onReportConfirmed?: VoidFunction
  onRetry?: VoidFunction
  isRetrying?: boolean
  className?: string
}

export function FeedbackUnknownError({
  layout = 'default',
  title,
  description,
  eventId,
  showReportButton = false,
  onReportConfirmed,
  onRetry,
  isRetrying = false,
  className,
}: FeedbackUnknownErrorProps) {
  const { getTranslation } = useTranslator()

  const defaultTitle = getTranslation(
    'common.messages.errors.unknownError.title',
  )

  const defaultDescription = getTranslation(
    'common.messages.errors.unknownError.description',
  )

  return (
    <Feedback layout={layout} variant="error" className={className}>
      <FeedbackImage
        src="/images/error/error-content.png"
        alt="Unknown Error"
        width={190}
        height={190}
      />

      <FeedbackContent>
        <FeedbackTitle>
          {layout === 'compact' && <AlertTriangleIcon />}
          {title || defaultTitle}
        </FeedbackTitle>

        <FeedbackDescription>
          {description || defaultDescription}
        </FeedbackDescription>

        {eventId && <FeedbackEventId eventId={eventId} />}
      </FeedbackContent>

      <div className={cn('flex flex-wrap items-center gap-2')}>
        {onRetry && (
          <FeedbackAction>
            <FeedbackRetryButton
              variant={layout === 'default' ? 'fintalk' : 'outline'}
              onRetry={onRetry}
              isRetrying={isRetrying}
            />
          </FeedbackAction>
        )}

        {showReportButton && (
          <FeedbackAction>
            <Button
              type="button"
              variant="fintalk"
              onClick={() => {
                openFeedbackDialog()
                onReportConfirmed?.()
              }}
            >
              <MessageCircleWarningIcon className="[&>*]:stroke-[2]" />
              {getTranslation('common.report')}
            </Button>
          </FeedbackAction>
        )}
      </div>
    </Feedback>
  )
}
